/**
 *
 */
import {
  createContext,
  useState,
  useContext,
  useEffect,
  PropsWithChildren,
} from 'react'
import {
  get,
  post,
  instance,
  TResponse,
  removeAuthToken,
  setAuthToken,
  getAuthToken,
  env,
  APP_CODE,
} from '@micun/web-components'
import qs from 'qs'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffectState } from '@micun/web-components/dist/utils/hooks/useEffectState'
import { LoadingOutlined } from '@ant-design/icons'
import { getBaseOrigin } from '@micun/web-components/dist/utils/common'

export type TAuthContext = {
  token: string
  setToken: (token: string) => void
  user: api.sso.User
  authorizing: boolean
  checkLogin(): Promise<boolean>
  logout(): void
}

export const loginByTicket = async (ticket: string) => {
  const { data } = await post<string>({
    url: `${getBaseOrigin('sso')}/sso/doLoginByTicket`,
    query: { ticket },
  })
  const nextSaToken = data
  return nextSaToken
}

export const AuthContext = createContext<TAuthContext>(null!)

export const USER_CACHE_KEY = 'user'

const defaultUser = () => ({
  userId: '',
  userCode: '',
  userName: '匿名用户',
  userPhone: '',
  identity: '',
  orgPathName: '',
  postName: '',
})

type TAppProviderProps = {} & PropsWithChildren

export function AuthProvider(props: TAppProviderProps) {
  const [token, setToken] = useEffectState(getAuthToken() || '')
  const [user, setUser] = useState<api.sso.User>(
    JSON.parse(localStorage[USER_CACHE_KEY] || 'null') || defaultUser()
  )

  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [authorizing, setAuthorizing] = useState(true)

  const doLogin = async () => {
    // if (location.pathname === '/login') {
    //   return
    // }

    let host = 'passport.micun.com'
    if (env) {
      host = `passport.${env}.micun.cn`
    }

    // https://passport.dev.micun.cn/?appCode=cbs&redirectUri=https://lowcode.dev.micun.cn/?back=https%3A%2F%2Flowcode.dev.micun.cn%2FForm%2FList
    navigate(
      `//${host}?${qs.stringify(
        {
          appCode: APP_CODE,
          redirectUri: encodeURIComponent(window.location.href),
        },
        { encode: false }
      )}`
    )
  }

  const isLogin = async () => {
    const { data } = await get<api.sso.isLogin>({
      url: '/sso/isLogin',
    })
    console.log('Auth: ', data)
    if (data.login) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user))
      setUser(data.user!)
      setAuthorizing(false)
    }
    return data.login
  }

  const checkLogin = async () => {
    const result = await isLogin()
    if (result) {
      // console.log('checkLogin redirect', location, token)
      if (location.pathname === '/login') {
        const redirect = decodeURIComponent(searchParams.get('redirect') || '/')
        navigate(redirect, {
          replace: true,
        })
      }
    } else {
      doLogin()
    }
    return result
  }

  const logout = async () => {
    await post({
      url: '/sso/signout',
    })

    removeAuthToken()
    localStorage.removeItem(USER_CACHE_KEY)
    setToken('')
    setUser(defaultUser())
    doLogin()
  }

  useEffect(() => {
    // console.log('update auto instance fetch', instance, token)
    let responseId: number
    responseId = instance.interceptors.response.use((response: any) => {
      // console.log('auth response', response)
      const { code } = response as TResponse
      if (code === 401) {
        return doLogin()
      }
      return response
    })

    return () => {
      instance.interceptors.response.eject(responseId)
    }
  }, [token])

  const doLoginAndRedirect = (token: string) => {
    setAuthToken(token)
    setToken(token)
    setTimeout(() => {
      checkLogin()
    })
  }

  useEffect(() => {
    const ticket = searchParams.getAll('ticket')
    if (Array.isArray(ticket) && ticket.length > 0) {
      console.log('auth ticket', ticket)
      const finalTicket = ticket[ticket.length - 1]
      loginByTicket(finalTicket).then((token) => {
        console.log('auth token', token)
        if (token) {
          searchParams.delete('ticket')
          setSearchParams(searchParams)
          return doLoginAndRedirect(token)
        } else {
          return doLogin()
        }
      })
    } else {
      checkLogin()
    }
  }, [])

  const state = {
    token,
    setToken: doLoginAndRedirect,
    user,
    authorizing,
    checkLogin,
    logout,
  }

  return <AuthContext.Provider {...props} value={state} />
}

export function useAuth() {
  return useContext(AuthContext)
}

export function RequireAuth(props: { children?: React.ReactNode }) {
  const { children } = props
  const auth = useAuth()

  if (auth.authorizing) {
    return (
      <div className='w-full h-full flex items-center justify-center'>
        <LoadingOutlined />
      </div>
    )
  }

  return children
}
